import { useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { api, ApiError } from "@/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectNoneItem,
  SelectTrigger,
  SelectValue,
  clearable,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import Pagination from "@/components/Pagination";
import { usePagination } from "@/hooks/usePagination";
import { useAuth } from "@/auth";
import {
  ACCOUNT_TYPES,
  TYPE_LABEL,
  type GlAccount,
} from "@/lib/gl";

interface FormState {
  id?: number;
  code: string;
  name: string;
  type: GlAccount["type"];
  subtype: string;
  normal_balance: GlAccount["normal_balance"];
  description: string;
  is_active: boolean;
}

const EMPTY_FORM: FormState = {
  code: "",
  name: "",
  type: "asset",
  subtype: "",
  normal_balance: "debit",
  description: "",
  is_active: true,
};

function typeBadge(t: GlAccount["type"]): string {
  const map: Record<GlAccount["type"], string> = {
    asset:
      "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30",
    liability:
      "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
    equity:
      "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
    revenue:
      "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30",
    expense:
      "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30",
  };
  return map[t];
}

interface AccountGroup {
  type: string;
  label: string;
  rows: GlAccount[];
}

function AccountGroupCard({
  group,
  canWrite,
  onEdit,
  onDelete,
}: {
  group: AccountGroup;
  canWrite: boolean;
  onEdit: (a: GlAccount) => void;
  onDelete: (a: GlAccount) => void;
}) {
  // Each account-type group paginates independently (10 rows/page) so a long
  // chart of accounts still fits on screen without scrolling.
  const pg = usePagination(group.rows);

  return (
    <Card className="shadow-card overflow-hidden">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Badge
            variant="outline"
            className={`capitalize ${typeBadge(group.type as GlAccount["type"])}`}
          >
            {group.label}
          </Badge>
          <span className="text-sm font-normal text-muted-foreground">
            {group.rows.length} accounts
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Subtype</TableHead>
              <TableHead>Normal</TableHead>
              <TableHead>Status</TableHead>
              {canWrite && (
                <TableHead className="w-[120px] text-right">Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pg.pageItems.map((a) => (
              <TableRow key={a.id}>
                <TableCell className="font-mono">{a.code}</TableCell>
                <TableCell className="font-medium">{a.name}</TableCell>
                <TableCell className="text-muted-foreground">
                  {a.subtype || "—"}
                </TableCell>
                <TableCell className="capitalize">{a.normal_balance}</TableCell>
                <TableCell>
                  {a.is_active ? (
                    <Badge
                      variant="outline"
                      className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
                    >
                      Active
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/30"
                    >
                      Inactive
                    </Badge>
                  )}
                </TableCell>
                {canWrite && (
                  <TableCell className="text-right">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => onEdit(a)}
                      title="Edit"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {a.is_active && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => onDelete(a)}
                        title="Deactivate"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {group.rows.length > 0 ? (
          <Pagination
            page={pg.page}
            pageCount={pg.pageCount}
            total={pg.total}
            from={pg.from}
            to={pg.to}
            onPrev={pg.prev}
            onNext={pg.next}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

export default function GeneralLedgerAccounts() {
  const { can } = useAuth();
  const canWrite = can("gl.write");

  const [accounts, setAccounts] = useState<GlAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [showInactive, setShowInactive] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.get<{ accounts: GlAccount[] }>("/gl/accounts");
      setAccounts(d.accounts);
    } catch (err) {
      toast.error((err as Error).message || "Failed to load accounts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return accounts.filter((a) => {
      if (!showInactive && !a.is_active) return false;
      if (typeFilter && typeFilter !== "all" && a.type !== typeFilter)
        return false;
      if (needle) {
        return (
          a.code.toLowerCase().includes(needle) ||
          a.name.toLowerCase().includes(needle)
        );
      }
      return true;
    });
  }, [accounts, q, typeFilter, showInactive]);

  const grouped = useMemo(() => {
    const m = new Map<string, GlAccount[]>();
    for (const a of filtered) {
      const arr = m.get(a.type) ?? [];
      arr.push(a);
      m.set(a.type, arr);
    }
    return ACCOUNT_TYPES.filter((t) => m.has(t.value)).map((t) => ({
      type: t.value,
      label: t.label,
      rows: (m.get(t.value) ?? []).sort((a, b) => a.code.localeCompare(b.code)),
    }));
  }, [filtered]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };
  const openEdit = (a: GlAccount) => {
    setForm({
      id: a.id,
      code: a.code,
      name: a.name,
      type: a.type,
      subtype: a.subtype ?? "",
      normal_balance: a.normal_balance,
      description: a.description ?? "",
      is_active: a.is_active,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const code = form.code.trim();
    const name = form.name.trim();
    if (!code) return toast.error("Code is required");
    if (!name) return toast.error("Name is required");
    if (!form.type) return toast.error("Type is required");
    if (!form.normal_balance) return toast.error("Normal balance is required");
    setSaving(true);
    try {
      const payload = {
        code,
        name,
        type: form.type,
        subtype: form.subtype.trim() || null,
        normal_balance: form.normal_balance,
        description: form.description.trim() || null,
        is_active: form.is_active,
      };
      if (form.id) {
        await api.put(`/gl/accounts/${form.id}`, payload);
        toast.success(`Updated ${code}`);
      } else {
        await api.post("/gl/accounts", payload);
        toast.success(`Created ${code}`);
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? String((err.body as { error?: string } | null)?.error ?? err.message)
          : (err as Error).message;
      toast.error(msg || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (a: GlAccount) => {
    if (!confirm(`Deactivate account ${a.code} — ${a.name}?`)) return;
    try {
      await api.del(`/gl/accounts/${a.id}`);
      toast.success("Account deactivated");
      await load();
    } catch (err) {
      toast.error((err as Error).message || "Delete failed");
    }
  };

  return (
    <div className="space-y-4">
      <Card className="shadow-card">
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-lg">Chart of Accounts</CardTitle>
            <CardDescription>
              Group accounts by type. Soft-delete keeps the account history
              intact and hides it from posting forms.
            </CardDescription>
          </div>
          {canWrite && (
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4 mr-1" />
              New Account
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by code or name"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={typeFilter} onValueChange={clearable(setTypeFilter)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectNoneItem />
                <SelectItem value="all">All types</SelectItem>
                {ACCOUNT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant={showInactive ? "default" : "outline"}
              size="sm"
              onClick={() => setShowInactive((s) => !s)}
            >
              {showInactive ? "Hide inactive" : "Show inactive"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card className="shadow-card">
          <CardContent className="py-10 text-center text-muted-foreground">
            Loading accounts…
          </CardContent>
        </Card>
      ) : grouped.length === 0 ? (
        <Card className="shadow-card">
          <CardContent className="py-10 text-center text-muted-foreground">
            No accounts match the current filter.
          </CardContent>
        </Card>
      ) : (
        grouped.map((group) => (
          <AccountGroupCard
            key={group.type}
            group={group}
            canWrite={canWrite}
            onEdit={openEdit}
            onDelete={handleDelete}
          />
        ))
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {form.id ? "Edit Account" : "New Account"}
            </DialogTitle>
            <DialogDescription>
              Account codes are unique and cannot be changed once the account
              has posted entries.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="code">Code</Label>
                <Input
                  id="code"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="1000"
                />
              </div>
              <div>
                <Label htmlFor="type">Type</Label>
                <Select
                  value={form.type || undefined}
                  onValueChange={clearable((v) => {
                    const def = ACCOUNT_TYPES.find((t) => t.value === v);
                    setForm({
                      ...form,
                      type: v as GlAccount["type"],
                      normal_balance: def?.normal ?? form.normal_balance,
                    });
                  })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectNoneItem />
                    {ACCOUNT_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Cash"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="subtype">Subtype</Label>
                <Input
                  id="subtype"
                  value={form.subtype}
                  onChange={(e) => setForm({ ...form, subtype: e.target.value })}
                  placeholder="current_asset"
                />
              </div>
              <div>
                <Label htmlFor="normal">Normal Balance</Label>
                <Select
                  value={form.normal_balance || undefined}
                  onValueChange={clearable((v) =>
                    setForm({
                      ...form,
                      normal_balance: v as GlAccount["normal_balance"],
                    })
                  )}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select normal balance" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectNoneItem />
                    <SelectItem value="debit">Debit</SelectItem>
                    <SelectItem value="credit">Credit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="desc">Description</Label>
              <Textarea
                id="desc"
                rows={2}
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                placeholder={`Optional notes about this ${(TYPE_LABEL[form.type] ?? "").toLowerCase()} account`}
              />
            </div>
            {form.id && (
              <div className="flex items-center gap-2 text-sm">
                <input
                  id="active"
                  type="checkbox"
                  className="h-4 w-4"
                  checked={form.is_active}
                  onChange={(e) =>
                    setForm({ ...form, is_active: e.target.checked })
                  }
                />
                <Label htmlFor="active">Active</Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : form.id ? "Save changes" : "Create account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
